import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import axios from 'axios'
import type { User, AuthStorage, ApplicationProfile } from '@/types'

const API_URL = process.env.VUE_APP_API_URL || 'http://optyfind-backend.localhost'

function loadFromStorage(): AuthStorage | null {
  try {
    return JSON.parse(localStorage.getItem('user') || 'null')
  } catch {
    return null
  }
}

interface StoredAuth extends AuthStorage {
  taxRate?: number
  taxLabel?: string | null
}

export const useAuthStore = defineStore('auth', () => {
  const _initial = loadFromStorage() as StoredAuth | null
  const user = ref<User | null>(_initial?.user || null)
  const token = ref<string | null>(_initial?.token || null)
  const geoTaxRate = ref<number>(_initial?.taxRate ?? 0)
  const geoTaxLabel = ref<string | null>(_initial?.taxLabel ?? null)

  const isAuthenticated = computed(() => !!token.value)

  function _persist(): void {
    if (token.value) {
      localStorage.setItem('user', JSON.stringify({
        user: user.value,
        token: token.value,
        taxRate: geoTaxRate.value,
        taxLabel: geoTaxLabel.value,
      }))
    } else {
      localStorage.removeItem('user')
    }
  }

  function _applyUserResponse(data: any): void {
    user.value = data.user
    geoTaxRate.value = data.user?.tax_rate ?? 0
    geoTaxLabel.value = data.user?.tax_label ?? null
  }

  async function login(email: string, password: string): Promise<unknown> {
    const res = await axios.post(`${API_URL}/auth/login`, { email, password }, { withCredentials: true })
    if (res.data.token) {
      token.value = res.data.token
      _applyUserResponse(res.data)
      _persist()
    }
    return res.data
  }

  async function logout(): Promise<void> {
    try {
      await axios.post(`${API_URL}/auth/logout`, {}, { withCredentials: true })
    } catch {
      console.log('Failed to logout')
    }
    token.value = null
    user.value = null
    geoTaxRate.value = 0
    geoTaxLabel.value = null
    localStorage.removeItem('user')
  }

  async function registerInitiate(firstName: string, lastName: string, email: string): Promise<unknown> {
    const res = await axios.post(`${API_URL}/auth/register/initiate`, {
      first_name: firstName,
      last_name: lastName,
      email,
    })
    return res.data
  }

  async function registerVerify(email: string, code: string): Promise<unknown> {
    const res = await axios.post(`${API_URL}/auth/register/verify`, { email, code })
    return res.data
  }

  async function registerComplete(
    email: string,
    password: string,
    profile: ApplicationProfile
  ): Promise<unknown> {
    const res = await axios.post(
      `${API_URL}/auth/register/complete`,
      { email, password, ...profile },
      { withCredentials: true }
    )
    if (res.data.token) {
      token.value = res.data.token
      _applyUserResponse(res.data)
      _persist()
    }
    return res.data
  }

  function setUser(userData: any): void {
    user.value = userData
    geoTaxRate.value = userData?.tax_rate ?? 0
    geoTaxLabel.value = userData?.tax_label ?? null
    _persist()
  }

  return {
    user, token, isAuthenticated,
    geoTaxRate, geoTaxLabel,
    login, logout, setUser,
    registerInitiate, registerVerify, registerComplete,
  }
})
